import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmpresaPerfilDto, EmpresaPerfilResponse } from '@triade/contracts';
import { ApiError, enviarArquivo, requisitarArquivo } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { useConsulta, useMutacao } from './base';

/** GET /empresa/perfil: dados do cabecalho do PDF do orcamento (qualquer papel le). */
export function useEmpresaPerfil() {
  return useConsulta<EmpresaPerfilResponse>(['empresa', 'perfil'], '/empresa/perfil');
}

/** PUT /empresa/perfil (ADMIN_TENANT e CONSULTOR). */
export function useSalvarEmpresaPerfil() {
  return useMutacao<EmpresaPerfilResponse, EmpresaPerfilDto>({
    metodo: 'PUT',
    caminho: () => '/empresa/perfil',
    corpo: (v) => v,
    invalidar: [['empresa']],
  });
}

/** PUT /empresa/logo (multipart). O servidor valida magic bytes e o limite de 512 KB. */
export function useEnviarLogo() {
  const qc = useQueryClient();
  return useMutation<EmpresaPerfilResponse, ApiError, File>({
    mutationFn: (arquivo) => enviarArquivo<EmpresaPerfilResponse>('/empresa/logo', arquivo, arquivo.name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empresa'] }),
  });
}

/** DELETE /empresa/logo. */
export function useRemoverLogo() {
  return useMutacao<EmpresaPerfilResponse>({ metodo: 'DELETE', caminho: () => '/empresa/logo', invalidar: [['empresa']] });
}

/**
 * Logo gravada como URL local (`blob:`) para <img>: o GET exige JWT, entao busca com o cliente da API e cria o objeto.
 * `chave` (ex.: atualizadoEm do perfil) refaz a busca quando a logo muda. Sem logo, `url` fica null.
 */
export function useLogoUrl(temLogo: boolean, chave: string | null) {
  const { empresaId } = useSessao();
  const q = useQuery<Blob | null, ApiError>({
    queryKey: ['empresa', 'logo', empresaId, chave],
    queryFn: async () => (await requisitarArquivo('/empresa/logo')).blob,
    enabled: empresaId !== null && temLogo,
    retry: false,
    staleTime: Infinity,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!temLogo || !q.data) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(q.data);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data, temLogo]);
  return { url: temLogo ? url : null, carregando: temLogo && q.isPending, erro: q.error };
}
